const DAY_MS = 24 * 60 * 60 * 1000;
const STALE_AFTER_DAYS = 30;

const RETIRED_STATUS = new Set([
  'retired',
  'decommissioned',
  'decom',
  'inactive',
  'disabled',
  'archived',
]);

function parseDate(value) {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function normalizeStatus(value) {
  if (value === null || value === undefined) return '';
  return String(value).trim().replace(/[\s-]+/g, '_').toLowerCase();
}

function textValue(value) {
  if (value === null || value === undefined) return '';
  return String(value).trim();
}

function numericValue(value) {
  if (value === null || value === undefined || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function lifecycleFromBackendStatus(status, asset, now) {
  const reason = textValue(asset?.lifecycleReason);
  const ageDays = numericValue(asset?.daysSinceLastObserved);
  const lastSeenAt = assetLastSeenAt(asset);
  const fallbackAgeDays = lastSeenAt ? Math.max(0, Math.floor((now.getTime() - lastSeenAt.getTime()) / DAY_MS)) : null;
  const observedAgeDays = ageDays ?? fallbackAgeDays;

  if (RETIRED_STATUS.has(status)) {
    return {
      key: 'retired',
      label: status === 'inactive' ? 'Inactive' : 'Retired',
      tone: 'retired',
      needsAction: false,
      ageDays: observedAgeDays,
      reason: reason || 'Backend lifecycle marks this asset retired or decommissioned.',
    };
  }

  if (status === 'stale') {
    return {
      key: 'stale',
      label: 'Stale',
      tone: 'stale',
      needsAction: true,
      ageDays: observedAgeDays,
      reason: reason || (observedAgeDays != null ? `Last observed ${observedAgeDays} days ago.` : 'Backend lifecycle marks this asset stale.'),
    };
  }

  if (status === 'active' || status === 'current') {
    return {
      key: 'active',
      label: 'Current',
      tone: 'active',
      needsAction: false,
      ageDays: observedAgeDays,
      reason: reason || (observedAgeDays != null ? 'Observed within the last 30 days.' : 'Backend lifecycle marks this asset current.'),
    };
  }

  if (status === 'unknown') {
    return {
      key: 'unknown',
      label: 'Unknown',
      tone: 'unknown',
      needsAction: false,
      ageDays: observedAgeDays,
      reason: reason || 'Backend lifecycle marks this asset unknown.',
    };
  }

  return null;
}

export function assetLastSeenAt(asset) {
  return parseDate(
    asset?.lastObservedAt ??
    asset?.lastObserved ??
    asset?.lastSeenAt ??
    asset?.lastSeen ??
    asset?.lastDetectedAt ??
    asset?.updatedAt
  );
}

export function assetStatusValue(asset) {
  return normalizeStatus(
    asset?.lifecycleStatus ??
    asset?.lifecycleState ??
    asset?.assetLifecycleStatus ??
    asset?.status ??
    asset?.state
  );
}

export function getAssetLifecycle(asset, now = new Date()) {
  const backendStatus = normalizeStatus(asset?.lifecycleStatus);
  const backendLifecycle = lifecycleFromBackendStatus(backendStatus, asset, now);
  if (backendLifecycle) return backendLifecycle;

  const status = assetStatusValue(asset);
  const explicitRetired = RETIRED_STATUS.has(status);
  const inactive = asset?.isActive === false;
  const fallbackReason = textValue(asset?.lifecycleReason);

  if (explicitRetired || inactive) {
    return {
      key: 'retired',
      label: explicitRetired ? 'Retired' : 'Inactive',
      tone: 'retired',
      needsAction: false,
      reason: fallbackReason || (explicitRetired ? 'API lifecycle/status marks this asset retired or decommissioned.' : 'API marks this asset inactive.'),
    };
  }

  const lastSeenAt = assetLastSeenAt(asset);
  if (lastSeenAt) {
    const ageDays = numericValue(asset?.daysSinceLastObserved) ?? Math.floor((now.getTime() - lastSeenAt.getTime()) / DAY_MS);
    if (ageDays >= STALE_AFTER_DAYS) {
      return {
        key: 'stale',
        label: 'Stale',
        tone: 'stale',
        needsAction: true,
        ageDays,
        reason: fallbackReason || `Last observed ${ageDays} days ago.`,
      };
    }
  }

  if (asset?.isActive === true || status === 'active' || lastSeenAt) {
    return {
      key: 'active',
      label: 'Current',
      tone: 'active',
      needsAction: false,
      ageDays: numericValue(asset?.daysSinceLastObserved) ?? (lastSeenAt ? Math.max(0, Math.floor((now.getTime() - lastSeenAt.getTime()) / DAY_MS)) : null),
      reason: fallbackReason || (lastSeenAt ? 'Observed within the last 30 days.' : 'API marks this asset active.'),
    };
  }

  return {
    key: 'unknown',
    label: 'Unknown',
    tone: 'unknown',
    needsAction: false,
    reason: fallbackReason || 'No lifecycle or last-observed fields were provided by the API.',
  };
}

export function summarizeAssetLifecycle(assets, now = new Date()) {
  return assets.reduce((acc, asset) => {
    const lifecycle = getAssetLifecycle(asset, now);
    acc[lifecycle.key] = (acc[lifecycle.key] ?? 0) + 1;
    if (lifecycle.needsAction) acc.needsAction += 1;
    return acc;
  }, { active: 0, stale: 0, retired: 0, unknown: 0, needsAction: 0 });
}

export const ASSET_LIFECYCLE_FILTERS = [
  { id: 'all', label: 'All lifecycle states' },
  { id: 'needsAction', label: 'Needs action' },
  { id: 'active', label: 'Current' },
  { id: 'stale', label: 'Stale' },
  { id: 'retired', label: 'Retired / inactive' },
  { id: 'unknown', label: 'Unknown' },
];
